import {
  API,
  deleteRequest,
  getRequest,
  getRequestBlob,
  patchRequest,
  postRequest,
  putRequest,
} from '@/api/api';

import {
  mapDtoToEquipment,
  mapDtoToEquipmentCategory,
  mapDtoToEquipmentDocument,
  mapDtoToEquipmentType,
  mapDtoToEquipmentTypeField,
  mapCreateUpdateEquipmentToDto,
  mapEquipmentToUpdateDto,
  type EquipmentFactsPatch,
} from './equipment.mappers';
import type {
  EquipmentCategoriesList,
  EquipmentCategoriesListDto,
  EquipmentDocumentDto,
  EquipmentsList,
  EquipmentsListDto,
  EquipmentTypeFieldDto,
  EquipmentTypesList,
  EquipmentTypesListDto,
  SingleEquipment,
  SingleEquipmentDto,
  CreateUpdateEquipmentFormFields,
  SetEquipmentCrewNotesDto,
} from './equipment.types';

/**
 * "No limit", as a limit — vessel requires the parameter and a fleet's equipment list is
 * a few dozen rows, so one page holds every item there is.
 */
export const ALL_EQUIPMENT_LIMIT = 99999;

export const getEquipmentCategories = async (
  page: number,
  limit: number,
  search: string,
): Promise<EquipmentCategoriesList> => {
  const { data } = await getRequest<EquipmentCategoriesListDto>(
    API.VESSEL,
    '/equipment-category',
    true,
    {
      page,
      limit,
      search,
    },
  );
  return {
    count: data.count,
    items: data.items.map(mapDtoToEquipmentCategory),
  };
};

export const getEquipmentTypes = async (categoryId: string): Promise<EquipmentTypesList> => {
  const { data } = await getRequest<EquipmentTypesListDto>(API.VESSEL, '/equipment-type', true, {
    limit: 99999,
    page: 1,
    category_id: categoryId,
  });
  return {
    count: data.count,
    items: data.items.map(mapDtoToEquipmentType),
  };
};

export const getEquipments = async (
  categoryId: string,
  page: number,
  limit: number,
  search: string,
): Promise<EquipmentsList> => {
  const { data } = await getRequest<EquipmentsListDto>(API.VESSEL, `/equipment`, true, {
    limit,
    page,
    category_id: categoryId,
    search,
  });

  return {
    count: data.count,
    items: data.items.map(mapDtoToEquipment),
  };
};

/**
 * Every item on the vessel, in one request.
 *
 * The lookup behind the maintenance surfaces: a task names its equipment by id, and the
 * category id needed to link to the item's page only lives on the equipment record. One
 * list request answers that for every row on the page — the alternative is a
 * `/equipment/{id}` per distinct id, which is a request count that grows with the list.
 *
 * `category_id` is omitted rather than sent blank: vessel binds the parameter into a
 * `*string` and filters on whatever it holds, so an empty one asks for the items in the
 * category named '' and answers with none.
 */
export const getAllEquipment = async (): Promise<SingleEquipment[]> => {
  const { data } = await getRequest<EquipmentsListDto>(API.VESSEL, '/equipment', true, {
    limit: ALL_EQUIPMENT_LIMIT,
    page: 1,
  });
  return data.items.map(mapDtoToEquipment);
};

export const getSingleEquipment = async (id: string) => {
  const { data } = await getRequest<SingleEquipmentDto>(API.VESSEL, `/equipment/${id}`);
  return mapDtoToEquipment(data);
};

/**
 * The item's PDF report — status, specs, the maintenance recorded against it,
 * crew notes and attached documents, rendered by vessel. Dates in it follow the
 * timezone passed; left off, vessel renders them in the vessel's own.
 */
export const getEquipmentPdf = async (id: string, timezone?: string): Promise<Blob> => {
  return getRequestBlob(API.VESSEL, `/equipment/${id}/pdf`, { timezone });
};

export const getEquipmentTypeFields = async (equipmentTypeId: string) => {
  const { data } = await getRequest<EquipmentTypeFieldDto[]>(
    API.VESSEL,
    '/equipment-type/structure-item',
    true,
    {
      equipment_type_id: equipmentTypeId,
    },
  );
  return data.map(mapDtoToEquipmentTypeField);
};

export const updateEquipment = async (id: string, values: CreateUpdateEquipmentFormFields) => {
  await putRequest(API.VESSEL, `/equipment/${id}`, mapCreateUpdateEquipmentToDto(values));
};

export const createEquipment = async (values: CreateUpdateEquipmentFormFields) => {
  const { data } = await postRequest<{ id: string }, unknown>(
    API.VESSEL,
    `/equipment`,
    mapCreateUpdateEquipmentToDto(values),
  );
  return data;
};

/**
 * The install date and the hour-meter reading, changed from the item's own page
 * without going through the edit form. The same PUT the form uses — vessel has
 * no narrower endpoint for these — so the item is sent back whole with only
 * these two touched.
 */
export const updateEquipmentFacts = async (
  equipment: SingleEquipment,
  patch: EquipmentFactsPatch,
) => {
  await putRequest(
    API.VESSEL,
    `/equipment/${equipment.id}`,
    mapEquipmentToUpdateDto(equipment, patch),
  );
};

export const deleteEquipment = async (id: string) => {
  await deleteRequest(API.VESSEL, `/equipment/${id}`);
};

/**
 * Appends documents to an item — vessel keeps up to `EQUIPMENT_DOCUMENTS_MAX` of
 * them and answers with the files it stored. Every file goes under the same
 * `files` key, which is how the multipart form carries a list. `request`
 * switches to multipart on its own for FormData.
 */
export const attachEquipmentDocuments = async (id: string, files: File[]) => {
  const formData = new FormData();
  files.forEach((file) => formData.append('files', file));
  const { data } = await patchRequest<EquipmentDocumentDto[], FormData>(
    API.VESSEL,
    `/equipment/${id}/document`,
    formData,
  );
  return data.map(mapDtoToEquipmentDocument);
};

/** Detaches one document, leaving the item's other files alone. */
export const deleteEquipmentDocument = async (id: string, documentId: string) => {
  await deleteRequest(API.VESSEL, `/equipment/${id}/document/${documentId}`);
};

/**
 * Overwrites the item's crew notes — vessel keeps a single blob per item, so
 * this replaces whatever was there rather than appending. Returns no content;
 * the new value comes back with the item.
 */
export const setEquipmentCrewNotes = async (id: string, crewNotes: string) => {
  await patchRequest<void, SetEquipmentCrewNotesDto>(API.VESSEL, `/equipment/${id}/crew-notes`, {
    crew_notes: crewNotes,
  });
};

export const deleteEquipmentCrewNotes = async (id: string) => {
  await deleteRequest(API.VESSEL, `/equipment/${id}/crew-notes`);
};
